import React, { Fragment, useState, useEffect } from "react";
import { HiOutlineUserCircle } from "react-icons/hi";
import { Popover, Transition, Menu } from "@headlessui/react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { auth, db } from "../../Auth/firebase";
import { signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useProjects } from "../../../context/ProjectsContext";
import { useAuth } from "../../../context/AuthContext";

export default function Header() {
  const [userProfileImage, setUserProfileImage] = useState("");
  const [imageLoading, setImageLoading] = useState(true);
  const [projectOptions, setProjectOptions] = useState([]);
  const { projects, activeProject, setActiveProject } = useProjects();
  const { currentUser } = useAuth();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const placeholderImage = "https://via.placeholder.com/150";

  useEffect(() => {
    const fetchUserProfileImage = async () => {
      try {
        const docRef = doc(db, "users", auth.currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userProfileData = docSnap.data();
          setUserProfileImage(userProfileData.profileImage);
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfileImage();
  }, []);

  useEffect(() => {
    if (userProfileImage) {
      const img = new Image();
      img.src = userProfileImage;
      img.onload = () => {
        setImageLoading(false);
      };
      img.onerror = () => {
        setUserProfileImage(placeholderImage);
        setImageLoading(false);
      };
    } else {
      setImageLoading(false);
    }
  }, [userProfileImage]);

  useEffect(() => {
    if (currentUser) {
      const filteredProjects = projects.filter(
        (project) =>
          project.creator.uid === currentUser.uid ||
          project.assignees.includes(currentUser.uid)
      );

      const projectOptions = filteredProjects.map((project) => ({
        value: project.id,
        label: project.name,
      }));

      setProjectOptions(projectOptions);

      if (projectOptions.length > 0 && !activeProject) {
        setActiveProject(filteredProjects[0]);
      }
    }
  }, [projects, currentUser, activeProject, setActiveProject]);

  const handleProjectChange = (selectedOption) => {
    const project = projects.find((p) => p.id === selectedOption.value);
    setActiveProject(project);
    // navigate(`/projects/${project.id}`);
    console.log("Selected project:", project);
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        console.log("User signed out");
        navigate("/login");
      })
      .catch((error) => {
        console.error("Error signing out:", error);
      });
  };

  return (
    <div className="bg-white h-16 px-4 flex justify-between items-center border-b-2 z-50">
      <div className="flex items-center space-x-3">
        <p className="text-base font-normal">Select Project</p>
        <div className="relative w-[24rem] z-50">
          <Select
            options={projectOptions}
            value={
              activeProject && {
                value: activeProject.id,
                label: activeProject.name,
              }
            }
            onChange={handleProjectChange}
            placeholder="Search or select a project..."
            classNamePrefix="react-select"
            styles={{
              control: (provided) => ({
                ...provided,
                borderRadius: "24px",
                border: "1px solid #D1D5DB",
                padding: "2px",
                boxShadow: "none",
              }),
              placeholder: (provided) => ({
                ...provided,
                color: "#6B7280",
              }),
            }}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 mr-3 z-50">
        <Menu as="div" className="relative inline-block text-left">
          <div>
            <Menu.Button className="ml-2 inline-flex items-center rounded-full focus:outline-none focus:ring-2 focus:ring-nutral-200">
              <span className="sr-only">Open user menu</span>
              <div
                style={{
                  backgroundImage: `url(${
                    imageLoading
                      ? placeholderImage
                      : userProfileImage || placeholderImage
                  })`,
                }}
                className="h-10 w-10 rounded-full bg-cover bg-no-repeat bg-center"
              >
                <span className="sr-only">Profile Image</span>
              </div>
            </Menu.Button>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="origin-top-right absolute z-10 right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
              <Menu.Item>
                {({ active }) => (
                  <div
                    onClick={() => navigate("/profile")}
                    className="cursor-pointer focus:bg-gray-100 hover:bg-gray-100 px-4 py-2 text-md flex items-center gap-3"
                  >
                    Your Profile
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <Link onClick={handleLogout}>
                    <div className="cursor-pointer focus:bg-gray-100 hover:bg-gray-100 px-4 py-2 text-md flex items-center gap-3">
                      Logout
                    </div>
                  </Link>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </div>
  );
}
